import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Leaf,
  TreeDeciduous,
  Palette,
  Droplets,
  Wind,
  Scissors,
  CheckCircle,
  ArrowRight,
  Phone,
} from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      icon: Leaf,
      title: 'Lawn Installation & Maintenance',
      description: 'Transform your yard with professional lawn installation and keep it looking pristine with our ongoing maintenance programs.',
      features: [
        'New lawn installation (sod or seed)',
        'Regular mowing and edging',
        'Fertilization programs',
        'Weed control treatments',
        'Aeration and overseeding',
        'Lawn disease management',
      ],
      image: 'https://images.pexels.com/photos/6728919/pexels-photo-6728919.jpeg?auto=compress&cs=tinysrgb&w=800',
    },
    {
      icon: Palette,
      title: 'Landscape Design',
      description: 'Custom landscape designs created specifically for your property, lifestyle, and the Pacific Northwest climate.',
      features: [
        'Custom design consultations',
        'Native plant selections',
        'Sustainable landscaping',
        'Outdoor living spaces',
        'Garden bed design',
        '3D design visualization',
      ],
      image: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?q=80&w=800&auto=format&fit=crop',
    },
    {
      icon: TreeDeciduous,
      title: 'Patio & Hardscape Installation',
      description: 'Create beautiful outdoor living areas with quality hardscape installations built to last.',
      features: [
        'Patio construction',
        'Walkway installation',
        'Retaining walls',
        'Outdoor fire pits',
        'Stone and paver work',
        'Decorative borders',
      ],
      image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=800&auto=format&fit=crop',
    },
    {
      icon: Droplets,
      title: 'Irrigation Systems',
      description: 'Efficient irrigation solutions designed to keep your landscape healthy while conserving water.',
      features: [
        'Sprinkler system installation',
        'Drip irrigation systems',
        'Smart controller installation',
        'System repairs and upgrades',
        'Winterization services',
        'Water efficiency audits',
      ],
      image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?q=80&w=800&auto=format&fit=crop',
    },
    {
      icon: Wind,
      title: 'Seasonal Yard Cleanup',
      description: 'Keep your property looking its best year-round with comprehensive seasonal cleanup services.',
      features: [
        'Spring cleanup and prep',
        'Fall leaf removal',
        'Debris clearing',
        'Gutter cleaning',
        'Garden bed preparation',
        'Winter storm cleanup',
      ],
      image: 'https://images.unsplash.com/photo-1509423350716-97f9360b4e09?q=80&w=800&auto=format&fit=crop',
    },
    {
      icon: Scissors,
      title: 'Tree & Shrub Trimming',
      description: 'Professional pruning and trimming to maintain the health, safety, and beauty of your plants.',
      features: [
        'Tree pruning and shaping',
        'Shrub trimming',
        'Hedge maintenance',
        'Storm damage cleanup',
        'Disease prevention',
        'Plant health assessments',
      ],
      image: 'https://images.unsplash.com/photo-1598902108854-10e335adac99?q=80&w=800&auto=format&fit=crop',
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#2E5C31]" data-testid="services-hero">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-['Poppins']">
            Our Landscaping Services
          </h1>
          <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto mb-8">
            Comprehensive landscaping solutions for residential properties in Seattle and surrounding areas.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact#estimate-form">
              <Button
                className="bg-[#D4E157] hover:bg-[#c5d24e] text-[#1A1A1A] rounded-full px-8 py-4 text-lg font-semibold shadow-lg transition-all duration-300"
                data-testid="services-cta-btn"
              >
                Get Free Estimate
              </Button>
            </Link>
            <a href="tel:+18888808889">
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-[#2E5C31] rounded-full px-8 py-4 text-lg font-semibold transition-all duration-300"
                data-testid="services-call-btn"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call Us Today
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20 md:py-28 bg-[#FAFAFA]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => (
              <div
                key={service.title}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`service-detail-${index}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-[#2E5C31] rounded-lg flex items-center justify-center">
                      <service.icon className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] font-['Poppins']">
                      {service.title}
                    </h2>
                  </div>
                  <p className="text-[#4A4A4A] mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#2E5C31] flex-shrink-0 mt-0.5" />
                        <span className="text-[#4A4A4A]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact#estimate-form">
                    <Button
                      variant="outline"
                      className="border-[#2E5C31] text-[#2E5C31] hover:bg-[#2E5C31] hover:text-white rounded-full px-6 py-3 font-semibold transition-all duration-300"
                    >
                      Request Estimate
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-80 lg:h-96 object-cover rounded-2xl shadow-xl"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Note */}
      <section className="py-16 bg-[#F2F5F3]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
          <p className="text-[#4A4A4A] max-w-3xl mx-auto">
            <strong className="text-[#1A1A1A]">Pricing Note:</strong> Service pricing varies based on property size, materials selected, and project scope. We provide detailed written estimates during your free consultation so you know exactly what to expect before any work begins.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#2E5C31]" data-testid="services-cta-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-['Poppins']">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto mb-8">
            Contact us today for a free consultation and estimate. We'll help you create the outdoor space you've always wanted.
          </p>
          <Link to="/contact#estimate-form">
            <Button
              className="bg-white text-[#2E5C31] hover:bg-[#D4E157] hover:text-[#1A1A1A] rounded-full px-10 py-4 text-lg font-semibold shadow-lg transition-all duration-300"
              data-testid="services-bottom-cta"
            >
              Get Your Free Estimate
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
